// Diaper check (care alert): a child flagged as using diapers has had no toilet
// entry for longer than the org's threshold. One workflow item of category
// diaper_check_overdue per child and day. Shared by the inbox row summary and the
// detail renderer.

interface TranslateWithValues {
  _: (d: { id: string; message: string; values?: Record<string, unknown> }) => string;
}

function readString(payload: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = payload?.[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/** Whole minutes between two RFC 3339 strings, or undefined when either does not parse. */
export function diaperCheckMinutes(
  payload: Record<string, unknown> | undefined,
  now: Date = new Date(),
): number | undefined {
  const since = Date.parse(readString(payload, 'since') ?? '');
  if (Number.isNaN(since)) return undefined;
  const endedRaw = Date.parse(readString(payload, 'ended_at') ?? '');
  const end = Number.isNaN(endedRaw) ? now.getTime() : endedRaw;
  return Math.max(0, Math.floor((end - since) / 60000));
}

/** "2 h 15 min", "2 h" or "45 min". */
export function formatDiaperDuration(i18n: TranslateWithValues, totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) {
    return i18n._({
      id: 'workflow.diaperCheck.duration.minutes',
      message: '{minutes} min',
      values: { minutes },
    });
  }
  if (minutes === 0) {
    return i18n._({
      id: 'workflow.diaperCheck.duration.hours',
      message: '{hours} h',
      values: { hours },
    });
  }
  return i18n._({
    id: 'workflow.diaperCheck.duration.hoursMinutes',
    message: '{hours} h {minutes} min',
    values: { hours, minutes },
  });
}

/** "for 2 h 15 min without a toilet entry", or undefined when `since` is missing. */
export function diaperCheckDurationLine(
  i18n: TranslateWithValues,
  payload: Record<string, unknown> | undefined,
  now: Date = new Date(),
): string | undefined {
  const minutes = diaperCheckMinutes(payload, now);
  if (minutes === undefined) return undefined;
  return i18n._({
    id: 'workflow.diaperCheck.durationLine',
    message: 'for {duration} without a toilet entry',
    values: { duration: formatDiaperDuration(i18n, minutes) },
  });
}

/** The inbox and board line: "Mia K. · Igel · for 2 h 15 min without a toilet entry". */
export function diaperCheckSummary(
  i18n: TranslateWithValues,
  payload: Record<string, unknown> | undefined,
  now: Date = new Date(),
): string | undefined {
  const parts = [
    readString(payload, 'child_name'),
    readString(payload, 'group_name'),
    diaperCheckDurationLine(i18n, payload, now),
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : undefined;
}
